'use client';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AuthUser, demoUsers, UserRole } from './mockData';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => { success: boolean; role?: UserRole; error?: string };
  loginAsDemo: (role: UserRole) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'bb_auth_user';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Rehydrate from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as AuthUser;
        setUser(parsed);
      }
    } catch {
      // ignore parse errors
    } finally {
      setIsLoading(false);
    }
  }, []);

  const persistUser = useCallback((u: AuthUser | null) => {
    setUser(u);
    if (u) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const login = useCallback((email: string, password: string) => {
    const found = demoUsers[email.toLowerCase()];
    if (found && password === 'demo123') {
      persistUser(found);
      return { success: true, role: found.role };
    }
    return { success: false, error: 'Invalid credentials — use the demo accounts below to sign in' };
  }, [persistUser]);

  const loginAsDemo = useCallback((role: UserRole) => {
    const emailMap: Record<UserRole, string> = {
      client: 'client@demo.com',
      contractor: 'contractor@demo.com',
      admin: 'admin@demo.com',
    };
    const found = demoUsers[emailMap[role]];
    if (found) persistUser(found);
  }, [persistUser]);

  const logout = useCallback(() => persistUser(null), [persistUser]);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, loginAsDemo, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}